import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { Menu, X, User, LogOut, ChevronDown } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import './Header.css'

const primaryNavLinks = [
  { path: '/', label: 'Home' },
  { path: '/services', label: 'Services' },
  { path: '/trainers', label: 'Our Trainers' },
  { path: '/schedule', label: 'Schedule' },
  { path: '/contact', label: 'Contact' }
]

const moreNavLinks = [
  { path: '/videos', label: 'Videos' },
  { path: '/live', label: 'Live' },
  { path: '/nutrition', label: 'Nutrition' },
  { path: '/community', label: 'Community' },
  { path: '/about', label: 'About' }
]

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const moreRef = useRef(null)
  const location = useLocation()
  const { user, logout, isAuthenticated } = useAuth()

  const isActive = (path) => location.pathname === path
  const isMoreActive = moreNavLinks.some((link) => location.pathname === link.path)

  useEffect(() => {
    setIsMoreOpen(false)
  }, [location.pathname])

  useEffect(() => {
    function handleClickOutside(e) {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setIsMoreOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    function handleKeyDown(e) {
      if (!isMoreOpen) return
      if (e.key === 'Escape') {
        setIsMoreOpen(false)
        moreRef.current?.querySelector('.nav-dropdown-trigger')?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isMoreOpen])

  useEffect(() => {
    if (isMoreOpen) {
      const firstItem = moreRef.current?.querySelector('[role="menuitem"]')
      firstItem?.focus()
    }
  }, [isMoreOpen])

  function handleMenuKeyDown(e) {
    if (!isMoreOpen) return
    const items = moreRef.current?.querySelectorAll('[role="menuitem"]')
    if (!items?.length) return
    const current = document.activeElement
    const currentIndex = Array.from(items).indexOf(current)
    if (e.key === 'ArrowDown' && currentIndex < items.length - 1) {
      e.preventDefault()
      items[currentIndex + 1].focus()
    } else if (e.key === 'ArrowUp' && currentIndex > 0) {
      e.preventDefault()
      items[currentIndex - 1].focus()
    } else if (e.key === 'ArrowUp' && currentIndex === 0) {
      e.preventDefault()
      moreRef.current?.querySelector('.nav-dropdown-trigger')?.focus()
    } else if (e.key === 'ArrowDown' && currentIndex === -1) {
      e.preventDefault()
      items[0].focus()
    }
  }

  return (
    <header className="header">
      <nav className="navbar">
        <div className="container">
          <div className="nav-content">
            <Link to="/" className="logo">
              <span className="logo-icon">R</span>
              <div className="logo-text">
                <span className="logo-title">RadianTech</span>
                <span className="logo-subtitle">FITNESS</span>
              </div>
            </Link>

            <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
              {primaryNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="nav-dropdown nav-dropdown-desktop" ref={moreRef}>
                <button
                  type="button"
                  className={`nav-dropdown-trigger nav-link ${isMoreActive ? 'active' : ''}`}
                  onClick={() => setIsMoreOpen(!isMoreOpen)}
                  aria-expanded={isMoreOpen}
                  aria-haspopup="menu"
                  aria-controls="nav-more-menu"
                  id="nav-more-trigger"
                >
                  More
                  <ChevronDown size={16} className={isMoreOpen ? 'nav-dropdown-chevron-open' : ''} />
                </button>
                {isMoreOpen && (
                  <div
                    id="nav-more-menu"
                    className="nav-dropdown-panel"
                    role="menu"
                    aria-labelledby="nav-more-trigger"
                    onKeyDown={handleMenuKeyDown}
                  >
                    {moreNavLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`nav-dropdown-link ${isActive(link.path) ? 'active' : ''}`}
                        onClick={() => setIsMenuOpen(false)}
                        role="menuitem"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {moreNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link nav-link-mobile-only ${isActive(link.path) ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="nav-actions-mobile">
                {isAuthenticated ? (
                  <>
                    <Link to="/account" onClick={() => setIsMenuOpen(false)} className="nav-actions-mobile-account nav-actions-mobile-link" aria-label="Account">
                      <User size={18} />
                    </Link>
                    {user?.isAdmin && <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="nav-actions-mobile-link">Admin</Link>}
                    <button onClick={() => { logout(); setIsMenuOpen(false); }} className="nav-actions-mobile-link">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)} className="nav-actions-mobile-link">Login</Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)} className="btn btn-outline nav-actions-mobile-link">Sign Up</Link>
                  </>
                )}
                <Link to="/book" onClick={() => setIsMenuOpen(false)} className="btn btn-primary nav-actions-mobile-book">Book Now</Link>
              </div>
            </div>

            <div className="nav-actions">
              {isAuthenticated ? (
                <>
                  <Link to="/account" className="nav-link nav-link-account">
                    <User size={18} />
                    <span className="nav-link-account-text">Account</span>
                  </Link>
                  {user?.isAdmin && (
                    <Link to="/admin" className="nav-link nav-link-admin">
                      Admin
                    </Link>
                  )}
                  <button onClick={logout} className="btn btn-outline btn-header btn-header-logout">
                    <LogOut size={18} />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="nav-link">Login</Link>
                  <Link to="/signup" className="btn btn-outline btn-header">Sign Up</Link>
                </>
              )}
              <Link to="/book" className="btn btn-primary btn-header">
                Book Now
              </Link>
              <button
                className="menu-toggle"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>
    </header>
  )
}

export default Header
